import {useAuthStore} from '@/store/auth-store';
import {sessionScope,sessionTenantId} from '@/features/auth/session-scope';
import {contextId} from './conversation-context-keys';
import { create } from 'zustand';

/**
 * BlazeConnector → BTS payment-proof pipeline (feature-flagged per tenant via
 * `blazeconnector` + `send_to_bts` — see `src/features/modules/queries.ts`).
 *
 * Flow: an agent sends a customer's receipt image to BTS
 * (`POST /inbox/conversations/{id}/send-to-bts {message_id}` → 202
 * `{status:'processing'}`), the connector validates it async, and the backend
 * pushes the outcome over WS as `inbox:bts_result`:
 *
 *   { conversation_id, message_id, status: 'approved'|'rejected'|'needs_review',
 *     amount: string, bank, payment_applied: bool, customerIdentified: bool }
 *
 * NOTE: amounts are STRINGS end-to-end (decimal-safe money) and the payload
 * carries NO currency — render `amount` verbatim, never parse/format it.
 */
export type BtsStatus = 'approved' | 'rejected' | 'needs_review';

/** WS payload / stored result. Every field defensive-optional — the handler
 *  must survive a partial payload from an older backend build. */
export interface BtsResult {
  conversation_id?: string;
  message_id?: string;
  status?: BtsStatus | string;
  /** Money as string; no currency field exists on the payload. */
  amount?: string;
  bank?: string;
  payment_applied?: boolean;
  customerIdentified?: boolean;
}

export interface FormattedBtsResult {
  icon: 'ok' | 'error' | 'warn';
  text: string;
}

/**
 * Menu-visibility predicate for "Enviar a BTS": only a CUSTOMER's (inbound)
 * image message qualifies as a payment receipt. An image is recognized by
 * `message_type === 'image'` OR an `image/*` media MIME (covers providers
 * that type receipts as generic attachments but carry the image MIME).
 * Pure + total — missing fields simply fail the check.
 */
export function isBtsEligibleMessage(msg: {
  direction?: string;
  message_type?: string;
  media_mime_type?: string;
}): boolean {
  if (msg.direction !== 'inbound') return false;
  return (
    msg.message_type === 'image' ||
    (typeof msg.media_mime_type === 'string' &&
      msg.media_mime_type.toLowerCase().startsWith('image/'))
  );
}

/** `"1500.00" → " — 1500.00"`, with the bank in parens when present. */
function amountBankSuffix(payload: BtsResult): string {
  const amount = typeof payload.amount === 'string' ? payload.amount.trim() : '';
  const bank = typeof payload.bank === 'string' ? payload.bank.trim() : '';
  let out = '';
  if (amount) out += ` — ${amount}`;
  if (bank) out += ` (${bank})`;
  return out;
}

/**
 * Human line for a BTS result — rendered in the in-conversation banner.
 * Pure and total: tolerates missing amount/bank/status (fields simply drop out
 * of the sentence). The payload has no currency, so `amount` is shown as-is.
 */
export function formatBtsResult(payload: BtsResult): FormattedBtsResult {
  const suffix = amountBankSuffix(payload);
  switch (payload.status) {
    case 'approved': {
      let text = `✅ Comprobante aprobado${suffix}.`;
      if (payload.payment_applied) text += ' Pago aplicado.';
      return { icon: 'ok', text };
    }
    case 'rejected': {
      let text = `❌ Comprobante rechazado${suffix}.`;
      if (payload.customerIdentified === false) text += ' Cliente no identificado.';
      return { icon: 'error', text };
    }
    case 'needs_review':
      return { icon: 'warn', text: `⚠️ Comprobante requiere revisión manual${suffix}.` };
    default:
      // Unknown/missing status — don't guess an outcome, but don't drop the
      // event either: surface it neutrally so the agent knows BTS answered.
      return { icon: 'warn', text: `⚠️ Resultado del comprobante recibido${suffix}.` };
  }
}

// ---------------------------------------------------------------------------
// Pure reducers (unit-tested; the Zustand store below just applies them).
// ---------------------------------------------------------------------------

/** Add an id to the sent-set immutably. Same reference back when already present
 *  so store subscribers don't re-render on duplicate marks. */
export function markSentReducer(sent: ReadonlySet<string>, messageId: string): ReadonlySet<string> {
  if (!messageId || sent.has(messageId)) return sent;
  const next = new Set(sent);
  next.add(messageId);
  return next;
}

/**
 * Append a result under its conversation immutably. A result with the same
 * `message_id` as an existing entry REPLACES it (the connector may re-emit,
 * e.g. needs_review → approved after manual review); otherwise it appends.
 * Results without a conversation_id can't be shown anywhere → dropped.
 */
export function addResultReducer(
  results: Record<string, BtsResult[]>,
  result: BtsResult,
): Record<string, BtsResult[]> {
  const convId = result.conversation_id;
  if (!convId) return results;
  const prev = results[convId] ?? [];
  const idx = result.message_id
    ? prev.findIndex((r) => r.message_id === result.message_id)
    : -1;
  const nextList =
    idx >= 0 ? prev.map((r, i) => (i === idx ? result : r)) : [...prev, result];
  return { ...results, [convId]: nextList };
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

interface BtsState {
  /** message ids already sent to BTS — disables the "Enviar a BTS" action for
   *  that message (both after the 202 and after a WS result lands). */
  sentMessageIds: ReadonlySet<string>;
  /** conversationId → results, shown as banners in the conversation; each
   *  result's `message_id` links it back to the receipt message. */
  results: Record<string, BtsResult[]>;
  markSent(messageId: string): void;
  isSent(messageId: string): boolean;
  addResult(result: BtsResult): void;
}

/**
 * Ephemeral, push-driven UI state (same store shape as `useAiSuggestionStore`).
 * Written from non-React code (the realtime manager, mutation onSuccess) via
 * `useBtsStore.getState()`; read from components via the hooks below.
 * Deliberately NOT persisted — results are session feedback, and the
 * billing/messages queries are the durable record.
 */
export const useBtsStore = create<BtsState>((set, get) => ({
  sentMessageIds: new Set<string>(),
  results: {},

  // No-op inputs bail out BEFORE set(): in Zustand, set({}) still produces a
  // new state object and notifies subscribers, so an empty-partial "no-op"
  // isn't actually free (Copilot PR#7 review). The reducers return the input
  // reference when nothing changes, so a cheap pre-check avoids the update.
  markSent: (messageId) => {
    const prev = get().sentMessageIds;
    const next = markSentReducer(prev, messageId);
    if (next !== prev) set({ sentMessageIds: next });
  },

  isSent: (messageId) => get().sentMessageIds.has(messageId),

  addResult: (result) => {
    const prev = get().results;
    const next = addResultReducer(prev, result);
    if (next !== prev) set({ results: next });
  },
}));

const EMPTY_RESULTS: BtsResult[] = [];

/** Stable-reference selector: the BTS results for one conversation (empty array
 *  — always the SAME empty array — when there are none, so memo deps hold). */
export function useBtsResults(conversationId: string | null | undefined): BtsResult[] {
  return useBtsStore((s) =>
    conversationId ? s.results[conversationId] ?? EMPTY_RESULTS : EMPTY_RESULTS,
  );
}

/** Reactive "already sent" flag for a message (disables the menu item). */
export function useBtsSent(messageId: string | null | undefined): boolean {
  return useBtsStore((s) => (messageId ? s.sentMessageIds.has(messageId) : false));
}

// Ephemeral feedback belongs to the account/tenant, not a rotated token.
useAuthStore.subscribe((next, previous) => {
  if (sessionScope(next) !== sessionScope(previous)) useBtsStore.setState({sentMessageIds:new Set<string>(),results:{}});
});

/** Native push is a hint under the current connection revision. No provider prose. */
export function readBtsResultHint(event: unknown): BtsResult | null {
  const object = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object' && !Array.isArray(value);
  if (!object(event) || !object(event.data)) return null;
  const data = event.data, state = useAuthStore.getState(), tenant = sessionTenantId(state);
  if (sessionScope(state) === 'anonymous' || !contextId(tenant) || !contextId(data.conversation_id)) return null;
  if ([event.tenant_id, data.tenant_id].some(declared => declared !== undefined && declared !== tenant) || event.conversation_id !== undefined && event.conversation_id !== data.conversation_id) return null;
  if (data.message_id !== undefined && !contextId(data.message_id) || ['status', 'amount', 'bank'].some(key => data[key] !== undefined && typeof data[key] !== 'string')) return null;
  if (['payment_applied', 'customerIdentified', 'customer_identified'].some(key => data[key] !== undefined && typeof data[key] !== 'boolean')) return null;
  const identified = data.customerIdentified ?? data.customer_identified;
  return { conversation_id: data.conversation_id,
    ...(typeof data.message_id === 'string' ? {message_id:data.message_id} : {}),
    ...(typeof data.status === 'string' ? {status:data.status} : {}),
    ...(typeof data.amount === 'string' ? {amount:data.amount} : {}),
    ...(typeof data.bank === 'string' ? {bank:data.bank} : {}),
    ...(typeof data.payment_applied === 'boolean' ? {payment_applied:data.payment_applied} : {}),
    ...(typeof identified === 'boolean' ? {customerIdentified:identified} : {}),
  };
}
